import {
	json,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { useEffect } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { Toaster, useToast } from '#app/ui/components'
import { GeneralErrorBoundary } from '#app/ui/components/feedback/error-boundary.tsx'
import { EpicProgress } from '#app/ui/components/primitives/progress-bar.tsx'
import { Footer } from '#app/ui/sections/footer.js'
import { appName } from '#app/utils/constants.ts'
/* import { useTheme } from './routes/resources+/theme-switch.tsx' */
import { stripeService } from './services/stripe/stripe.server.ts'
import fontStyleSheetUrl from './styles/fonts.css?url'
import './styles/tailwind.css'
import { Header } from './ui/sections/header/header.tsx'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { PermissionProvider } from './utils/permission-context.tsx'
import { transformUserPermissions } from './utils/permissions-utils.ts'
import { type Theme /* , getTheme */ } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: LinksFunction = () => {
	return [
		{ rel: `mask-icon`, href: `/favicons/mask-icon.svg` },
		{
			rel: `alternate icon`,
			type: `image/png`,
			href: `/favicons/favicon-32x32.png`,
		},
		{ rel: `apple-touch-icon`, href: `/favicons/apple-touch-icon.png` },
		{
			rel: `manifest`,
			href: `/site.webmanifest`,
			crossOrigin: `use-credentials`,
		} as const, // necessary to make typescript happy
		{ rel: `icon`, type: `image/svg+xml`, href: `/favicons/favicon.svg` },
		{ rel: `preload`, href: fontStyleSheetUrl, as: `style` },
		{ rel: `stylesheet`, href: fontStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? appName : `Error | ${appName}` },
		{ name: `description`, content: `Your own captain's log` },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings(`root loader`)
	const userId = await time(() => getUserId(request), {
		timings,
		type: `getUserId`,
		desc: `getUserId in root`,
	})
	const isStripeEnabled: Boolean = stripeService.stripe != null
	const subscriptionsEnabled: Boolean = stripeService.subscriptionsEnabled

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							image: { select: { id: true } },
							platformRoles: {
								select: {
									platformRole: {
										select: {
											name: true,
											permissions: {
												select: { entity: true, action: true, access: true },
											},
										},
									},
								},
							},
							nightclubRoles: {
								select: {
									nightclubRole: {
										select: {
											name: true,
											permissions: {
												select: { entity: true, action: true, access: true },
											},
										},
									},
									nightclubId: true,
								},
							},
							eventRoles: {
								select: {
									eventRole: {
										select: {
											name: true,
											permissions: {
												select: { entity: true, action: true, access: true },
											},
										},
									},
									nightclubId: true,
									eventId: true,
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: `find user`, desc: `find user in root` },
			)
		: null
	if (userId && !user) {
		console.info(`something weird happened`)
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: `/` })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				/* userPrefs: {
					theme: getTheme(request),
				}, */
			},
			ENV: getEnv(),
			toast,
			honeyProps,
			isStripeEnabled,
			subscriptionsEnabled,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get(`Server-Timing`) ?? ``,
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = `light`,
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}) {
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
				/>
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				{env.CLARITY_PROJECT_ID ? (
					<script
						nonce={nonce}
						dangerouslySetInnerHTML={{
							__html: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${env.CLARITY_PROJECT_ID}");`,
						}}
					/>
				) : null}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				{env.GA_TRACKING_ID ? (
					<script
						nonce={nonce}
						dangerouslySetInnerHTML={{
							__html: [
								`window.dataLayer=window.dataLayer||[];`,
								`window.gtag=function(){window.dataLayer.push(arguments)};`,
								`window.gtag('js',new Date());`,
								`window.gtag('config','${env.GA_TRACKING_ID}'${env.MODE !== `production` ? `,{debug_mode:true,traffic_type:'internal'}` : ``});`,
							].join(``),
						}}
					/>
				) : null}
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function useGoogleAnalyticsScript(trackingId: string | undefined) {
	useEffect(() => {
		if (!trackingId) return
		const script = document.createElement(`script`)
		script.async = true
		script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`
		document.head.appendChild(script)
	}, [trackingId])
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	/* const theme = useTheme() */
	const allowIndexing = data.ENV.ALLOW_INDEXING !== `false`
	useToast(data.toast)
	useGoogleAnalyticsScript(data.ENV.GA_TRACKING_ID)

	return (
		<Document
			nonce={nonce}
			/* theme={theme} */
			allowIndexing={allowIndexing}
			env={data.ENV}
		>
			<div className="flex min-h-screen flex-col justify-between gap-6">
				<Header />

				<div className="flex-1">
					<Outlet />
				</div>

				<Footer />
			</div>
			<Toaster
				closeButton
				position="top-center"
				/* theme={theme} */
				toastOptions={{
					classNames: {
						closeButton: `toaster-close-button`,
					},
				}}
			/>
			<EpicProgress />
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	const userPermissions = data.user ? transformUserPermissions(data.user) : null

	return (
		<HoneypotProvider {...data.honeyProps}>
			<PermissionProvider permissions={userPermissions}>
				<App />
			</PermissionProvider>
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
